/** @badge Custom — show/hide-обёртка с фиксированным пресетом pop-in/fade-out
 *  @when   Badge то показывает, то прячет счётчик при смене count без скачка layout
 *  @not    нужен другой пресет/своя длительность (slide, scale, blur) — это Animate (feedback/)
 *  @breaks unmount отложен таймером на 150ms — быстрый show→hide→show ловит гонку animState
 */
"use client";

import { ReactNode, useState, useEffect, useRef } from "react";

interface AnimatedWrapperProps {
  show: boolean;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export function AnimatedWrapper({ show, children, className, style }: AnimatedWrapperProps) {
  const [visible, setVisible] = useState(show);
  // "idle" = mounted with show=true, no animation
  const [animState, setAnimState] = useState<"enter" | "exit" | "idle">(show ? "idle" : "exit");
  const mounted = useRef(false);
  
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    
    if (show) {
      setVisible(true);
      setAnimState("enter");
    } else {
      setAnimState("exit");
      // matches --cg-duration-fast (150ms) used by the exit animation below
      const t = setTimeout(() => setVisible(false), 150);
      return () => clearTimeout(t);
    }
  }, [show]);
  
  if (!visible) return null;
  
  // Keyframes (kit-aw-*) live globally in app/globals.css — no per-mount <style> injection.
  const animation = animState === "enter"
    ? "kit-aw-pop-in var(--cg-duration-fast) var(--cg-ease-default) forwards"
    : animState === "exit"
    ? "kit-aw-fade-out var(--cg-duration-fast) var(--cg-ease-default) forwards"
    : "none";

  return (
    <div className={className} style={{ ...style, animation }}>
      {children}
    </div>
  );
}
